import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Toaster, toast } from 'sonner';
import { 
  Lock, Save, LogOut, Users, FileText, Archive,
  BarChart3, RefreshCw, UserPlus, Activity,
  PieChart, Bell, Lightbulb, Home, Eye
} from 'lucide-react';
import axios from 'axios';
import { AnalyticsTab } from './admin/tabs/AnalyticsTab';
import { ExecutiveTab } from './admin/tabs/ExecutiveTab';
import { ActivityTab } from './admin/tabs/ActivityTab';
import { InvitesTab } from './admin/tabs/InvitesTab';
import { SettingsTab } from './admin/tabs/SettingsTab';
import { TahTemplateTab } from './admin/tabs/TahTemplateTab';
import { ImpersonateTab } from './admin/tabs/ImpersonateTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;


const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [facilityItems, setFacilityItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');
  const [managerRequests, setManagerRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState(null);
  
  // Activity logs state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activitySummary, setActivitySummary] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logFilter, setLogFilter] = useState('');
  const [logEventType, setLogEventType] = useState('');
  
  // Active users state
  const [activeUsers, setActiveUsers] = useState(null);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  
  // Executive report state
  const [executiveReport, setExecutiveReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Feedback state
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [updatingFeedback, setUpdatingFeedback] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});
  
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [newAnnouncementTitle, setNewAnnouncementTitle] = useState('');
  const [newAnnouncementMessage, setNewAnnouncementMessage] = useState('');
  const [creatingAnnouncement, setCreatingAnnouncement] = useState(false);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);

  // TAH Template state
  const [tahTemplate, setTahTemplate] = useState(null);
  const [loadingTahTemplate, setLoadingTahTemplate] = useState(false);
  const [newTahItemName, setNewTahItemName] = useState('');
  const [creatingTahItem, setCreatingTahItem] = useState(false);
  const [deletingTahItem, setDeletingTahItem] = useState(null);

  // Impersonation state
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [impersonating, setImpersonating] = useState(null);

  // Diagnostics state
  const [diagnosticEmail, setDiagnosticEmail] = useState('');
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);
  const [restoringTactical, setRestoringTactical] = useState(null);
  const [deletingTactical, setDeletingTactical] = useState(null);

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
    // Run once on mount; setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data when logged in
  useEffect(() => {
    if (isLoggedIn && token) {
      loadFacilityItems();
      loadAnalytics();
      loadActiveUsers();
      loadManagerRequests();
      loadActivityLogs();
    }
    // Load functions are stable closures over `token`; effect re-runs when token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    
    try {
      const response = await axios.post(`${API_BASE}/admin/login`, {
        username,
        password,
      });
      
      const { token: adminToken } = response.data;
      localStorage.setItem('admin_token', adminToken);
      setToken(adminToken);
      setIsLoggedIn(true);
      setUsername('');
      setPassword('');
      toast.success('Login successful');
    } catch (err) {
      console.error('Login failed:', err);
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setIsLoggedIn(false);
    setFacilityItems([]);
    setAnalytics(null);
    setManagerRequests([]);
    toast.success('Logged out');
  };

  const loadActiveUsers = async () => {
    setLoadingActiveUsers(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/active-users?authorization=Bearer ${token}`);
      setActiveUsers(response.data);
    } catch (err) {
      console.error('Active users load failed:', err);
    } finally {
      setLoadingActiveUsers(false);
    }
  };

  // Auto-refresh active users every 30 seconds
  useEffect(() => {
    if (isLoggedIn && token) {
      const interval = setInterval(() => {
        loadActiveUsers();
      }, 30000);
      return () => clearInterval(interval);
    }
    // loadActiveUsers reads the latest `token` via closure each tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, token]);

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/analytics?authorization=Bearer ${token}`);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Analytics load failed:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadExecutiveReport = async () => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/executive-report?authorization=Bearer ${token}`);
      setExecutiveReport(response.data);
    } catch (err) {
      console.error('Executive report load failed:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoadingReport(false);
    }
  };

  const exportReportToPDF = () => {
    // Create printable version
    const printWindow = window.open('', '_blank');
    const reportHtml = generateReportHTML();
    printWindow.document.write(reportHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const generateReportHTML = () => {
    if (!executiveReport) return '';
    const r = executiveReport;
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>TMC Therapy Growth Hub - Executive Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #0f766e; border-bottom: 3px solid #0f766e; padding-bottom: 10px; }
          h2 { color: #115e59; margin-top: 30px; }
          .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin: 20px 0; }
          .stat-box { background: #f8fafc; border: 1px solid #e2e8f0; padding: 20px; border-radius: 8px; text-align: center; }
          .stat-value { font-size: 32px; font-weight: bold; color: #0f766e; }
          .stat-label { font-size: 14px; color: #64748b; margin-top: 5px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #e2e8f0; padding: 12px; text-align: left; }
          th { background: #0f766e; color: white; }
          tr:nth-child(even) { background: #f8fafc; }
          .health-bar { display: flex; height: 30px; border-radius: 4px; overflow: hidden; margin: 10px 0; }
          .health-green { background: #10b981; }
          .health-yellow { background: #f59e0b; }
          .health-red { background: #ef4444; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>TMC Therapy Growth Hub - Executive Report</h1>
        <p>Generated: ${new Date(r.generated_at).toLocaleString()}</p>
        
        <h2>Summary Overview</h2>
        <div class="stat-grid">
          <div class="stat-box">
            <div class="stat-value">${r.summary.total_users}</div>
            <div class="stat-label">Total Users</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${r.summary.active_tacticals}</div>
            <div class="stat-label">Active Tacticals</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${r.summary.archived_tacticals}</div>
            <div class="stat-label">Archived Tacticals</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${r.summary.manager_relationships}</div>
            <div class="stat-label">Manager Relationships</div>
          </div>
        </div>
        
        <h2>Growth Metrics</h2>
        <table>
          <tr><th>Period</th><th>New Users</th><th>Active Logins</th><th>Tacticals Created</th></tr>
          <tr><td>Last 30 Days</td><td>${r.user_growth['30_days']}</td><td>${r.login_activity['30_days']}</td><td>${r.tactical_creation['30_days']}</td></tr>
          <tr><td>Last 60 Days</td><td>${r.user_growth['60_days']}</td><td>${r.login_activity['60_days']}</td><td>${r.tactical_creation['60_days']}</td></tr>
          <tr><td>Last 90 Days</td><td>${r.user_growth['90_days']}</td><td>${r.login_activity['90_days']}</td><td>${r.tactical_creation['90_days']}</td></tr>
          <tr><td>Last 12 Months</td><td>${r.user_growth.yearly}</td><td>${r.login_activity.yearly}</td><td>${r.tactical_creation.yearly}</td></tr>
        </table>
        
        <h2>Scorecard Health</h2>
        <div class="health-bar">
          <div class="health-green" style="width: ${r.scorecard_health.green_pct}%"></div>
          <div class="health-yellow" style="width: ${r.scorecard_health.yellow_pct}%"></div>
          <div class="health-red" style="width: ${r.scorecard_health.red_pct}%"></div>
        </div>
        <p>Green: ${r.scorecard_health.green_pct}% | Yellow: ${r.scorecard_health.yellow_pct}% | Red: ${r.scorecard_health.red_pct}%</p>
        
        <h2>Manager Engagement</h2>
        <table>
          <tr><th>Metric</th><th>Count</th></tr>
          <tr><td>Total Manager Relationships</td><td>${r.manager_engagement.total_relationships}</td></tr>
          <tr><td>Unique Managers</td><td>${r.manager_engagement.unique_managers}</td></tr>
          <tr><td>Employees with Managers</td><td>${r.manager_engagement.unique_employees_with_managers}</td></tr>
          <tr><td>Pending Invitations</td><td>${r.manager_engagement.pending_invitations}</td></tr>
        </table>
        
        <h2>Top Engaged Users</h2>
        <table>
          <tr><th>Name</th><th>Email</th><th>Total Tacticals</th></tr>
          ${r.top_engaged_users.map(u => `<tr><td>${u.name}</td><td>${u.email}</td><td>${u.tactical_count}</td></tr>`).join('')}
        </table>
        
        <h2>System Health</h2>
        <p>Activity Events (30 days): ${r.system_health.activity_events_30d}</p>
        <p>Errors (30 days): ${r.system_health.errors_30d}</p>
        <p>Error Rate: ${r.system_health.error_rate}%</p>
        
        <div class="footer">
          <p>TMC Therapy Growth Hub - Confidential Executive Report</p>
          <p>© ${new Date().getFullYear()} Therapy Management Corporation</p>
        </div>
      </body>
      </html>
    `;
  };

  const loadManagerRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/manager-requests?authorization=Bearer ${token}`);
      setManagerRequests(response.data.requests || []);
    } catch (err) {
      console.error('Manager requests load failed:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!confirm('Are you sure you want to delete this manager request? This action cannot be undone.')) {
      return;
    }
    
    setDeletingRequest(requestId);
    try {
      await axios.delete(`${API_BASE}/admin/manager-requests/${requestId}?authorization=Bearer ${token}`);
      setManagerRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Manager request deleted');
      // Refresh analytics to update counts
      loadAnalytics();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete request');
    } finally {
      setDeletingRequest(null);
    }
  };

  const loadActivityLogs = async (filter = '', eventType = '') => {
    setLoadingLogs(true);
    try {
      let url = `${API_BASE}/admin/activity-logs?authorization=Bearer ${token}&limit=100`;
      if (filter) url += `&user_email=${encodeURIComponent(filter)}`;
      if (eventType) url += `&event_type=${encodeURIComponent(eventType)}`;
      
      const [logsRes, summaryRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API_BASE}/admin/activity-logs/summary?authorization=Bearer ${token}`)
      ]);
      
      setActivityLogs(logsRes.data.logs || []);
      setActivitySummary(summaryRes.data);
    } catch (err) {
      console.error('Activity logs load failed:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleCleanupLogs = async () => {
    if (!window.confirm('Delete activity logs older than 90 days?')) return;
    try {
      const response = await axios.post(`${API_BASE}/admin/activity-logs/cleanup?authorization=Bearer ${token}`);
      toast.success(response.data.message);
      loadActivityLogs(logFilter, logEventType);
    } catch (err) {
      toast.error('Failed to cleanup logs');
    }
  };

  // Announcements functions
  const loadAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/announcements?authorization=Bearer ${token}`);
      setAnnouncements(response.data || []);
    } catch (err) {
      console.error('Failed to load announcements:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!newAnnouncementTitle.trim() || !newAnnouncementMessage.trim()) {
      toast.error('Please fill in both title and message');
      return;
    }
    
    setCreatingAnnouncement(true);
    try {
      await axios.post(`${API_BASE}/admin/announcements?authorization=Bearer ${token}`, {
        title: newAnnouncementTitle,
        message: newAnnouncementMessage,
      });
      toast.success('Announcement created successfully');
      setNewAnnouncementTitle('');
      setNewAnnouncementMessage('');
      loadAnnouncements();
    } catch (err) {
      toast.error('Failed to create announcement');
    } finally {
      setCreatingAnnouncement(false);
    }
  };

  const handleToggleAnnouncement = async (announcementId, currentActive) => {
    try {
      await axios.put(`${API_BASE}/admin/announcements/${announcementId}?authorization=Bearer ${token}`, {
        is_active: !currentActive,
      });
      toast.success(`Announcement ${currentActive ? 'deactivated' : 'activated'}`);
      loadAnnouncements();
    } catch (err) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Delete this announcement? Users who already acknowledged it will not see it again.')) return;
    
    setDeletingAnnouncement(announcementId);
    try {
      await axios.delete(`${API_BASE}/admin/announcements/${announcementId}?authorization=Bearer ${token}`);
      toast.success('Announcement deleted');
      loadAnnouncements();
    } catch (err) {
      toast.error('Failed to delete announcement');
    } finally {
      setDeletingAnnouncement(null);
    }
  };

  // Feedback functions
  const loadFeedback = async (status = feedbackFilter) => {
    setLoadingFeedback(true);
    try {
      // Only add status filter if not "all"
      const url = status && status !== 'all'
        ? `${API_BASE}/admin/feedback?status=${status}&authorization=Bearer ${token}`
        : `${API_BASE}/admin/feedback?authorization=Bearer ${token}`;
      const [feedbackRes, statsRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API_BASE}/admin/feedback/stats?authorization=Bearer ${token}`)
      ]);
      setFeedbackList(feedbackRes.data || []);
      setFeedbackStats(statsRes.data || {});
    } catch (err) {
      console.error('Failed to load feedback:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleUpdateFeedbackStatus = async (feedbackId, newStatus) => {
    setUpdatingFeedback(feedbackId);
    try {
      await axios.put(`${API_BASE}/admin/feedback/${feedbackId}/status?authorization=Bearer ${token}`, {
        status: newStatus,
        admin_notes: adminNotes[feedbackId] || null,
      });
      toast.success(`Feedback marked as ${newStatus}`);
      loadFeedback(feedbackFilter);
    } catch (err) {
      toast.error('Failed to update feedback status');
    } finally {
      setUpdatingFeedback(null);
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Delete this feedback submission?')) return;
    
    try {
      await axios.delete(`${API_BASE}/admin/feedback/${feedbackId}?authorization=Bearer ${token}`);
      toast.success('Feedback deleted');
      loadFeedback(feedbackFilter);
    } catch (err) {
      toast.error('Failed to delete feedback');
    }
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      login: 'bg-green-100 text-green-800',
      login_failed: 'bg-red-100 text-red-800',
      invitation_sent: 'bg-blue-100 text-blue-800',
      invitation_accepted: 'bg-emerald-100 text-emerald-800',
      invitation_rejected: 'bg-orange-100 text-orange-800',
      invitation_expired: 'bg-yellow-100 text-yellow-800',
      tactical_created: 'bg-purple-100 text-purple-800',
      tactical_updated: 'bg-indigo-100 text-indigo-800',
      admin_action: 'bg-slate-100 text-slate-800',
      error: 'bg-red-100 text-red-800',
    };
    return colors[eventType] || 'bg-gray-100 text-gray-800';
  };

  const formatLogDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadFacilityItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/facility-template?authorization=Bearer ${token}`);
      const items = response.data.facility_items || [];
      const paddedItems = [...items];
      while (paddedItems.length < 6) {
        paddedItems.push('');
      }
      setFacilityItems(paddedItems);
    } catch (err) {
      console.error('Load failed:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load facility items');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index, value) => {
    setFacilityItems(prev => {
      const newItems = [...prev];
      newItems[index] = value;
      return newItems;
    });
  };

  const handleAddItem = () => {
    if (facilityItems.length >= 10) {
      toast.error('Maximum 10 items allowed');
      return;
    }
    setFacilityItems(prev => [...prev, '']);
  };

  const handleRemoveItem = (index) => {
    if (facilityItems.filter(i => i.trim()).length <= 1) {
      toast.error('At least one item is required');
      return;
    }
    setFacilityItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const nonEmptyItems = facilityItems.filter(item => item.trim());
    
    if (nonEmptyItems.length === 0) {
      toast.error('At least one facility item is required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE}/admin/facility-template?authorization=Bearer ${token}`,
        { facility_items: nonEmptyItems }
      );
      toast.success('Facility template saved successfully');
      loadFacilityItems();
    } catch (err) {
      console.error('Save failed:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  // TAH Template functions
  const loadTahTemplate = async () => {
    setLoadingTahTemplate(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/tah-template?authorization=Bearer ${token}`);
      setTahTemplate(response.data || []);
    } catch (err) {
      console.error('Load TAH template failed:', err);
      toast.error('Failed to load TAH template');
    } finally {
      setLoadingTahTemplate(false);
    }
  };

  const handleAddTahItem = async () => {
    if (!newTahItemName.trim()) {
      toast.error('Please enter a name for the TAH item');
      return;
    }
    
    setCreatingTahItem(true);
    try {
      await axios.post(`${API_BASE}/admin/tah-template?authorization=Bearer ${token}`, {
        name: newTahItemName.trim()
      });
      toast.success('TAH template item added');
      setNewTahItemName('');
      loadTahTemplate();
    } catch (err) {
      console.error('Add TAH item failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to add TAH item');
    } finally {
      setCreatingTahItem(false);
    }
  };

  const handleDeleteTahItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this TAH template item?')) {
      return;
    }
    
    setDeletingTahItem(itemId);
    try {
      await axios.delete(`${API_BASE}/admin/tah-template/${itemId}?authorization=Bearer ${token}`);
      toast.success('TAH template item deleted');
      loadTahTemplate();
    } catch (err) {
      console.error('Delete TAH item failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete TAH item');
    } finally {
      setDeletingTahItem(null);
    }
  };

  // Impersonation functions
  const loadUsers = async (search = '') => {
    setLoadingUsers(true);
    try {
      const url = search
        ? `${API_BASE}/admin/users?q=${encodeURIComponent(search)}&authorization=Bearer ${token}`
        : `${API_BASE}/admin/users?authorization=Bearer ${token}`;
      const response = await axios.get(url);
      setUsersList(response.data.users || []);
    } catch (err) {
      console.error('Load users failed:', err);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleImpersonate = async (userId, displayName) => {
    setImpersonating(userId);
    try {
      const response = await axios.post(
        `${API_BASE}/admin/impersonate/${userId}?authorization=Bearer ${token}`
      );
      const { token: userToken, user: impersonatedUser } = response.data;
      
      // Save admin token so we can return
      sessionStorage.setItem('admin_return_token', token);
      sessionStorage.setItem('impersonating_user', JSON.stringify(impersonatedUser));
      
      // Set the user token and redirect to dashboard
      localStorage.setItem('tactical_token', userToken);
      
      toast.success(`Now viewing as ${displayName}`);
      
      // Navigate to the main app
      window.location.href = window.location.origin;
    } catch (err) {
      console.error('Impersonate failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to impersonate user');
    } finally {
      setImpersonating(null);
    }
  };

  // Diagnostics functions
  const runDiagnostic = async (email) => {
    const searchEmail = email || diagnosticEmail;
    if (!searchEmail.trim()) {
      toast.error('Enter an email address');
      return;
    }
    setLoadingDiagnostic(true);
    try {
      const resp = await axios.get(
        `${API_BASE}/admin/user-tacticals-diagnostic?email=${encodeURIComponent(searchEmail)}&authorization=Bearer ${token}`
      );
      setDiagnosticResult(resp.data);
      if (resp.data.total_tacticals === 0) {
        toast.info('No tacticals found for this email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  const handleRestoreTactical = async (tacticalId) => {
    if (!confirm('This will restore this archived tactical to active. If there is a conflicting active tactical for the same month, it will be archived. Continue?')) return;
    setRestoringTactical(tacticalId);
    try {
      const resp = await axios.post(
        `${API_BASE}/admin/restore-tactical/${tacticalId}?authorization=Bearer ${token}`
      );
      toast.success(resp.data.message);
      runDiagnostic(diagnosticEmail);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoringTactical(null);
    }
  };

  const handleArchiveTactical = async (tacticalId, year, month) => {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    if (!confirm(`Archive this ${monthNames[month-1]} ${year} tactical? It will appear in the user's Past Tacticals.`)) return;
    setRestoringTactical(tacticalId);
    try {
      const resp = await axios.post(
        `${API_BASE}/admin/archive-tactical/${tacticalId}?authorization=Bearer ${token}`
      );
      toast.success(resp.data.message);
      runDiagnostic(diagnosticEmail);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    } finally {
      setRestoringTactical(null);
    }
  };

  const handleDeleteTactical = async (tacticalId, year, month) => {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    if (!confirm(`PERMANENTLY delete this ${monthNames[month-1]} ${year} tactical? This cannot be undone.`)) return;
    setDeletingTactical(tacticalId);
    try {
      const resp = await axios.delete(
        `${API_BASE}/admin/tactical/${tacticalId}?authorization=Bearer ${token}`
      );
      toast.success(resp.data.message);
      runDiagnostic(diagnosticEmail);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingTactical(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 border-green-300">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 border-red-300">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingRequests = managerRequests.filter(r => r.status === 'pending');
  const approvedRequests = managerRequests.filter(r => r.status === 'approved');
  const rejectedRequests = managerRequests.filter(r => r.status === 'rejected');

  // Login form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Admin Login</CardTitle>
            <CardDescription>
              TMC Therapy Growth Hub Administration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter admin username"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="mt-1"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loggingIn}>
                {loggingIn ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">TMC Therapy Growth Hub</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => { loadAnalytics(); loadManagerRequests(); }} disabled={loadingAnalytics}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingAnalytics ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="executive" className="flex items-center gap-2" onClick={() => !executiveReport && loadExecutiveReport()}>
              <PieChart className="w-4 h-4" />
              Executive Report
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Activity Logs
            </TabsTrigger>
            <TabsTrigger value="invites" className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Manager Invites
              {pendingRequests.length > 0 && (
                <Badge className="ml-1 bg-yellow-100 text-yellow-800 text-xs px-1.5">
                  {pendingRequests.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Facility Template
            </TabsTrigger>
            <TabsTrigger value="tah-template" className="flex items-center gap-2" onClick={() => !tahTemplate && loadTahTemplate()}>
              <Home className="w-4 h-4" />
              TAH Template
            </TabsTrigger>
            <TabsTrigger value="impersonate" className="flex items-center gap-2" onClick={() => usersList.length === 0 && loadUsers()}>
              <Eye className="w-4 h-4" />
              Impersonate
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <AnalyticsTab activeUsers={activeUsers} analytics={analytics} loadActiveUsers={loadActiveUsers} loadingActiveUsers={loadingActiveUsers} loadingAnalytics={loadingAnalytics} />

          {/* Executive Report Tab */}
          <ExecutiveTab analytics={analytics} executiveReport={executiveReport} exportReportToPDF={exportReportToPDF} loadExecutiveReport={loadExecutiveReport} loadingReport={loadingReport} />

          {/* Activity Logs Tab */}
          <ActivityTab activityLogs={activityLogs} activitySummary={activitySummary} formatLogDate={formatLogDate} getEventTypeColor={getEventTypeColor} handleCleanupLogs={handleCleanupLogs} loadActivityLogs={loadActivityLogs} loadingLogs={loadingLogs} logEventType={logEventType} logFilter={logFilter} setLogEventType={setLogEventType} setLogFilter={setLogFilter} />

          {/* Manager Invites Tab */}
          <InvitesTab approvedRequests={approvedRequests} deletingRequest={deletingRequest} getStatusBadge={getStatusBadge} handleDeleteRequest={handleDeleteRequest} loadingRequests={loadingRequests} managerRequests={managerRequests} pendingRequests={pendingRequests} rejectedRequests={rejectedRequests} />

          {/* Settings Tab */}
          <SettingsTab facilityItems={facilityItems} handleAddItem={handleAddItem} handleItemChange={handleItemChange} handleRemoveItem={handleRemoveItem} handleSave={handleSave} loading={loading} saving={saving} token={token} />
          
          {/* TAH Template Tab */}
          <TahTemplateTab creatingTahItem={creatingTahItem} deletingTahItem={deletingTahItem} handleAddTahItem={handleAddTahItem} handleDeleteTahItem={handleDeleteTahItem} loadTahTemplate={loadTahTemplate} loadingTahTemplate={loadingTahTemplate} newTahItemName={newTahItemName} setNewTahItemName={setNewTahItemName} tahTemplate={tahTemplate} />

          {/* Impersonate Tab */}
          <ImpersonateTab deletingTactical={deletingTactical} diagnosticEmail={diagnosticEmail} diagnosticResult={diagnosticResult} handleArchiveTactical={handleArchiveTactical} handleDeleteTactical={handleDeleteTactical} handleImpersonate={handleImpersonate} handleRestoreTactical={handleRestoreTactical} impersonating={impersonating} loadUsers={loadUsers} loadingDiagnostic={loadingDiagnostic} loadingUsers={loadingUsers} restoringTactical={restoringTactical} runDiagnostic={runDiagnostic} setDiagnosticEmail={setDiagnosticEmail} setUserSearch={setUserSearch} userSearch={userSearch} usersList={usersList} />
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;